import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useDispatch, useSelector } from 'react-redux'
import {
  getProgramEligibility,
  getProgramRequirements,
  getProgramParticipation,
  getProgramVisibility,
  getProgramLoadGroups,
} from '@/actions/admin'
import { adminUtilityProgramCollection } from '@/actions/adminUtiltyProgramsCollections'
import Loader from '../../components/layout/Loader'
import ProgramSelector from '../../components/Program/ProgramSelector'
import ProfileEligibilitySummary from '../../components/Program/ProfileEligibilitySummary'
import DeviceCheckResults from '../../components/Program/DeviceCheckResults'
import LoadGroupsList from '../../components/Program/ProgramLoadGroupList'
import ProgramOverview from '../../components/Program/ProgramOverview'

function formatLoadGroups(data) {
  const { load_group_definitions, load_group_customer_ux_descriptions } = data

  return load_group_definitions.map((group) => {
    // Find UX description (only demand_response for now)
    const ux =
      load_group_customer_ux_descriptions?.demand_response?.[group.identifier]
        ?.default || 'No description provided.'

    // Determine which device types this group applies to
    const deviceTypes = []

    if (group.vehicle_collectors?.length) deviceTypes.push('Vehicles')
    if (group.charger_collectors?.length) deviceTypes.push('Chargers')
    if (group.thermostat_collectors?.length) deviceTypes.push('Thermostats')
    if (group.energy_system_collectors?.length)
      deviceTypes.push('Energy Systems')

    return {
      name: group.name,
      identifier: group.identifier,
      description: ux,
      deviceTypes,
      settings: group.settings,
      scope_tags: group.scope_tags,
    }
  })
}

export default function UtilityEligibility({ profileID = '' }) {
  const dispatch = useDispatch()
  const [utilityProgram, setUtilityProgram] = useState(-1)

  const isLoading = useSelector((state) => state.admin.isLoading)
  const programEligibility = useSelector(
    (state) => state.admin.utilityEligibility,
  )
  const programRequirements = useSelector(
    (state) => state.admin.programRequirements,
  )
  const programParticipation = useSelector(
    (state) => state.admin.programParticipation,
  )
  const programVisibility = useSelector(
    (state) => state.admin.programVisibility,
  )
  const programLoadGroups = useSelector(
    (state) => state.admin.programLoadGroups,
  )

  const { data: utilityPrograms = [], isLoading: isProgramsLoading } =
    adminUtilityProgramCollection.useFetch()

  useEffect(() => {
    if (utilityProgram !== -1) {
      dispatch(getProgramRequirements(profileID, utilityProgram))
      dispatch(getProgramLoadGroups(profileID, utilityProgram))
      dispatch(getProgramEligibility(profileID, utilityProgram))
      dispatch(getProgramParticipation(profileID, utilityProgram))
      dispatch(getProgramVisibility(profileID, utilityProgram))
    }
  }, [utilityProgram])

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Program Debugger
      </Typography>
      <Card variant="outlined">
        <CardContent>
          {isProgramsLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={80}
            >
              <CircularProgress />
            </Box>
          ) : (
            <ProgramSelector
              utilityPrograms={utilityPrograms.sort((a, b) =>
                a.name.localeCompare(b.name),
              )}
              selected={utilityProgram}
              onChange={(value) => setUtilityProgram(value)}
              disabled={utilityPrograms.length === 0}
            />
          )}
        </CardContent>
      </Card>

      {programRequirements && (
        <ProgramOverview programRequirements={programRequirements} />
      )}

      {programLoadGroups && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Program Load Groups</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <LoadGroupsList
                loadGroups={formatLoadGroups(programLoadGroups)}
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {programEligibility && (
        <Box mt={3}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Eligibility Checks</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Profile Level Eligibility
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ProfileEligibilitySummary
                    eligibility={programEligibility.profile}
                  />
                </AccordionDetails>
              </Accordion>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Device Level Eligibility Checks
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DeviceCheckResults devices={programEligibility.devices} />
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      {programVisibility && (
        <Box mt={3}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Visibility Checks</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Program Visibility Checks
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ProfileEligibilitySummary eligibility={programVisibility} />
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {programParticipation && (
        <Box mt={3}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Participation Checks</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Device Level Participation Checks
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DeviceCheckResults devices={programParticipation} />
                </AccordionDetails>
              </Accordion>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {isLoading && <Loader />}
    </Box>
  )
}
